<template>
  <div ref="gallery" class="tw-space-y-3" :class="{ 'tw-mb-8': !noMargin }">
    <div class="tw-relative">
      <div
        :class="Classes.HERO_IMAGE"
        class="mod-util-ratio-16-9 tw-relative tw-w-full game-tile-background-color tw-overflow-hidden game-tile-radius"
      >
        <div
          class="tw-size-full tw-overflow-hidden tw-transform tw-transition tw-absolute"
        >
          <button
            v-if="items[activeId]?.type === 'image'"
            class="tw-absolute tw-top-0 tw-left-0 tw-size-full tw-outline-none tw-bg-cover tw-bg-center"
            :style="`background-image: url(${items[activeId].preview})`"
            @click.prevent="showPreview"
          />

          <view-iframe v-else :link="items[activeId]?.link" />
        </div>
      </div>

      <div
        v-if="modId && purchased"
        class="tw-absolute tw-top-px tw-left-px xs:tw-scale-125 xl:tw-scale-150"
      >
        <purchased-label type="card" />
      </div>
    </div>

    <slider v-if="items.length > 1" ref="slider">
      <div class="tw-flex tw-w-full tw-gap-1">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="[
            `${Classes.GALLERY_IMAGE} tw-h-full tw-shrink-0`,
            items.length > 5 ? 'tw-w-1/5' : 'tw-w-[calc(20%-0.2rem)]',
          ]"
        >
          <thumbnail-item
            :item="item"
            :class="
              index === activeId
                ? 'game-primary-button-border-color'
                : 'tw-border-transparent'
            "
            @item:update="updateItem(index)"
            @item:keydown="handleKeyPress"
          />
        </div>
      </div>
    </slider>
  </div>

  <teleport to="body">
    <image-preview-modal
      :show="isModalOpen"
      :files="items"
      :image-id="imageId"
      @input:keypress="handlePreviewKeypress"
      @change:image="(val) => move(val < 0)"
      @close:modal="hideModal"
    >
      <template #slider>
        <div
          class="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-px-[4%] md:tw-px-[10%] tw-transition-max-height tw-duration-300 tw-overflow-hidden tw-max-h-44"
        >
          <slider v-if="items.length > 1" ref="modalSlider">
            <div class="tw-flex tw-w-fit tw-mx-auto tw-justify-center tw-gap-1">
              <div
                v-for="(item, index) in items"
                :key="index"
                class="tw-h-full tw-shrink-0 tw-w-36"
              >
                <thumbnail-item
                  :item="item"
                  :class="
                    index === activeId
                      ? 'game-primary-button-border-color'
                      : 'tw-border-transparent'
                  "
                  @item:update="updatePreviewItem(index)"
                />
              </div>
            </div>
          </slider>
        </div>
      </template>
    </image-preview-modal>
  </teleport>
</template>

<script>
import ImagePreviewModal from '@components/Modals/ImagePreviewModal.vue'
import PurchasedLabel from '@shared/components/Misc/PurchasedLabel.vue'
import ThumbnailItem from '@components/Slider/ThumbnailItem.vue'
import ViewIframe from '@shared/components/View/ViewIframe.vue'
import Slider from '@shared/components/Slider/Slider.vue'
import { onMounted, computed, toRefs, ref } from 'vue'
import Classes from '@shared/js/config/css.js'
import { onClickOutside } from '@vueuse/core'
import { purchasesStore } from '@stores'
import { useModal } from '@composables'

const AUTOPLAY_TIMEOUT = 2000

export default {
  components: {
    ImagePreviewModal,
    PurchasedLabel,
    ThumbnailItem,
    ViewIframe,
    Slider,
  },
  props: {
    autoPlay: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      required: true,
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
    modId: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const { autoPlay, items, modId } = toRefs(props)

    const galleryFocus = ref(false)
    const modalSlider = ref(null)
    const timeout = ref(null)
    const imageId = ref(null)
    const gallery = ref(null)
    const slider = ref(null)
    const activeId = ref(0)

    const { modOwned } = purchasesStore()

    const { hideModal, showModal, isModalOpen } = useModal()

    const purchased = computed(() => modOwned(modId.value))

    onMounted(() => {
      _autoPlay()
      onClickOutside(gallery, _focusOutGallery)
    })

    function updateItem(index) {
      activeId.value = index
      scrollTo(true)
      _clearTimeout()
    }

    function updatePreviewItem(index) {
      updateItem(index)
      imageId.value = activeId.value
    }

    function move(previous = false) {
      activeId.value =
        (activeId.value + (previous ? -1 : 1) + items.value.length) %
        items.value.length
      if (isModalOpen.value) updateItem(activeId.value)
    }

    function handleKeyPress(e) {
      e.preventDefault()
      switch (e.key) {
        case 'Tab':
          e.shiftKey ? move(true) : move()
          break
        case 'ArrowLeft':
          move(true)
          break
        case 'ArrowRight':
          move()
          break
      }

      scrollTo(true)
    }

    function showPreview() {
      _focusInGallery()
      if (items.value.length) {
        imageId.value = activeId.value
        showModal()
      }
    }

    function _focusInGallery() {
      galleryFocus.value = true
    }

    function _focusOutGallery() {
      galleryFocus.value = false
    }

    function _resetAutoplay() {
      setTimeout(() => {
        activeId.value = 0

        _autoPlay()
      }, AUTOPLAY_TIMEOUT)
    }

    function _autoPlay() {
      if (autoPlay.value) {
        timeout.value = setTimeout(() => {
          move()

          if (activeId.value + 1 < items.value.length) {
            _autoPlay()
          } else {
            _resetAutoplay()
          }
        }, AUTOPLAY_TIMEOUT)
      }
    }

    function _clearTimeout() {
      if (autoPlay.value) {
        clearTimeout(timeout.value)
      }
    }

    function handlePreviewKeypress(e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Tab') {
        scrollTo(true)
      }
    }

    function sliderScrollIntoView(slider, thumbnails) {
      if (!thumbnails || !slider) return

      const thumbEl = thumbnails[activeId.value]

      slider.target.scrollTo({
        left:
          thumbEl.offsetLeft -
          slider.target.clientWidth / 2 +
          thumbEl.offsetWidth / 2,
        behavior: 'smooth',
      })
    }

    function handleObserver(thumbnail, observer) {
      if (thumbnail.intersectionRatio < 1) {
        scrollTo()
      }
      observer.disconnect()
    }

    function createOverflowObserver() {
      return new IntersectionObserver(
        ([thumbnail], observer) => handleObserver(thumbnail, observer),
        { threshold: [0, 1] }
      )
    }

    function scrollTo(checkOverflow) {
      const sliderThumbEl =
        slider.value?.target?.querySelectorAll('.tw-shrink-0')
      const modalThumbEl =
        modalSlider.value?.target?.querySelectorAll('.tw-shrink-0')
      if (checkOverflow) {
        createOverflowObserver().observe(sliderThumbEl[activeId.value])
        if (modalThumbEl) {
          createOverflowObserver().observe(modalThumbEl[activeId.value])
        }
        return
      }
      sliderScrollIntoView(slider.value, sliderThumbEl)
      sliderScrollIntoView(modalSlider.value, modalThumbEl)
    }

    return {
      handlePreviewKeypress,
      updatePreviewItem,
      handleKeyPress,
      showPreview,
      modalSlider,
      isModalOpen,
      updateItem,
      purchased,
      hideModal,
      activeId,
      imageId,
      gallery,
      Classes,
      slider,
      move,
    }
  },
}
</script>
